import { useState } from 'react';
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer } from 'recharts';
import ScoreRing from './ScoreRing';

const TABS = ['overview', 'skills', 'gaps', 'suggestions'];

export default function MatchResults({ result, jobTitle, company }) {
  const [tab, setTab] = useState('overview');
  const { overallScore, verdict, summary, categoryScores, matchedSkills, missingSkills,
    extraSkills, strengths, gaps, suggestions, atsScore, experienceYears, seniorityMatch } = result;

  const verdictBadge = overallScore >= 70 ? 'badge-green' : overallScore >= 45 ? 'badge-yellow' : 'badge-red';

  const radarData = Object.entries(categoryScores || {}).map(([key, val]) => ({
    subject: key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()),
    value: val
  }));

  const suggestionIcon = (t) => t === 'add' ? '➕' : t === 'improve' ? '🔧' : '✨';

  return (
    <div className="card">
      {/* Header */}
      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 24 }}>
        <div style={{ flex: 1 }}>
          {(company || jobTitle) && <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>{company}{company && jobTitle && ' · '}{jobTitle}</div>}
          <div style={{ fontFamily: 'Syne', fontSize: 24, fontWeight: 800, marginBottom: 8 }}>
            {verdict} <span className={`badge ${verdictBadge}`} style={{ fontSize: 11 }}>{overallScore >= 70 ? 'Recommended' : overallScore >= 45 ? 'Review' : 'Needs Work'}</span>
          </div>
          <p style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.8, maxWidth: 560 }}>{summary}</p>
          <div style={{ display: 'flex', gap: 20, marginTop: 16, flexWrap: 'wrap' }}>
            {[['🎯', 'ATS Score', atsScore + '%'], ['📅', 'Experience', experienceYears], ['📊', 'Seniority', seniorityMatch]].map(([ico, lbl, val]) => (
              <div key={lbl} style={{ fontSize: 13 }}>
                {ico} <span style={{ color: 'var(--muted)' }}>{lbl}:</span>{' '}
                <span style={{ color: 'var(--accent2)', fontWeight: 600 }}>{val}</span>
              </div>
            ))}
          </div>
        </div>
        <ScoreRing score={overallScore} />
      </div>

      <hr style={{ border: 'none', borderTop: '1px solid var(--border)', marginBottom: 24 }} />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24 }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            fontFamily: 'Syne', fontWeight: 700, fontSize: 13, padding: '8px 16px',
            borderRadius: 8, cursor: 'pointer', border: '1px solid', transition: 'all 0.2s',
            background: tab === t ? 'var(--surface2)' : 'transparent',
            borderColor: tab === t ? 'var(--border)' : 'transparent',
            color: tab === t ? 'var(--text)' : 'var(--muted)'
          }}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>
        ))}
      </div>

      {tab === 'overview' && (
        <div>
          <div className="two-col">
            <div>
              <div className="section-title">Strengths</div>
              {(strengths || []).map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, fontSize: 13, lineHeight: 1.6, background: 'var(--surface2)', padding: '12px 14px', borderRadius: 8 }}>
                  <span>💪</span>{s}
                </div>
              ))}
            </div>
            <div>
              <div className="section-title">Radar Chart</div>
              <ResponsiveContainer width="100%" height={200}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="var(--border)" />
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: 'var(--muted)' }} />
                  <Radar dataKey="value" stroke="#7c6af7" fill="#7c6af7" fillOpacity={0.2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {tab === 'skills' && (
        <div>
          {[['Matched Skills ✅', matchedSkills, 'tag-green'], ['Missing Skills ❌', missingSkills, 'tag-red'], ['Bonus Skills ⭐', extraSkills, 'tag-purple']].map(([title, skills, cls]) =>
            skills?.length > 0 && (
              <div key={title} style={{ marginBottom: 24 }}>
                <div className="section-title">{title}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {skills.map(s => <span key={s} className={`tag ${cls}`}>{s}</span>)}
                </div>
              </div>
            )
          )}
        </div>
      )}

      {tab === 'gaps' && (
        <div>
          <div className="section-title">Areas to Improve</div>
          {(gaps || []).map((g, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, fontSize: 13, lineHeight: 1.6, background: 'var(--surface2)', padding: '12px 14px', borderRadius: 8 }}>
              <span>⚡</span>{g}
            </div>
          ))}
        </div>
      )}

      {tab === 'suggestions' && (
        <div>
          <div className="section-title">Resume Improvements</div>
          {(suggestions || []).map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10, fontSize: 13, lineHeight: 1.6, background: 'var(--surface2)', padding: '14px 16px', borderRadius: 8 }}>
              <span style={{ fontSize: 16 }}>{suggestionIcon(s.type)}</span>
              <div>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--muted)', marginBottom: 4 }}>{s.type}</div>
                {s.text}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
